<template>
  <div class="card shadow mt-2 mb-3 border-0" v-if="resources.length">
    <div class="card-body">
      <div class="title">
        <div class="wrap">
          <h5>Настройки автоответов
            <popper hover
                    content="Для указанных источников и ссылок отмеченных на отслеживание будут отправляться автоматические ответы на новые отзывы">
              <i class="bi bi-info pointer"></i>
            </popper>
          </h5>
          <div class="sub-title">Автооответы доступны для отслеживаемых ссылок, поддерживаются источники:
            {{ resources.map(x => x.name).join(',') }}
          </div>
        </div>
      </div>
      <div class="auto-reply-area">
        <div class="resource-item" v-for="resource in resources" :key="resource.id">
          <div class="resource">
            <div class="resource-ico">
              <span class="img" v-if="resource?.ico"
                    :style="{backgroundImage: `url(${resource.ico})`}"></span>
              <span class="img no-image" v-else :style="{backgroundColor: resource?.color}"></span>
              <span class="name">{{ resource?.name || resource?.domain }}</span>
            </div>
          </div>

          <div v-if="resource.settings && resource.settings.length" class="settings">
            <div class="settings-group">
              <div
                v-for="(setting, index) in resource.settings"
                :key="setting._tempId || setting.id || index"
                class="setting-item"
              >
                <div class="setting-header">
                  <span class="setting-index">
                    Настройка #{{ index + 1 }}
                    <span v-if="setting.isNew" class="badge badge-warning">Новая</span>
                    <span v-else-if="setting.id" class="badge badge-success">Сохранена</span>
                  </span>
                  <button
                    @click.prevent="deleteSetting(resource, setting)"
                    class="btn btn-sm btn-outline-danger"
                    :title="setting.isNew ? 'Удалить настройку' : 'Удалить настройку'"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>

                <div class="text-section">
                  <div class="form-group">
                    <label class="settings-label">Текст автоответа</label>
                    <textarea
                      v-model="setting.text"
                      class="form-control"
                      placeholder="Введите текст автоответа"
                      rows="3"
                    ></textarea>
                  </div>

                  <div class="form-group" v-if="hasTextNoDataAccess">
                    <label class="settings-label">Текст при отсутствии данных</label>
                    <textarea
                      v-model="setting.text_no_data"
                      class="form-control"
                      placeholder="Введите текст при отсутствии данных"
                      rows="2"
                    ></textarea>
                  </div>
                </div>

                <div class="controls-section">
                  <div class="checkbox">
                    <input
                      :id="'active_'+resource.id+'_'+index"
                      type="checkbox"
                      v-model="setting.is_active"
                    >
                    <label :for="'active_'+resource.id+'_'+index">Активный</label>
                  </div>
                  <div class="checkbox">
                    <input
                      :id="'war_'+resource.id+'_'+index"
                      type="checkbox"
                      v-model="setting.war_marker"
                    >
                    <label :for="'war_'+resource.id+'_'+index">Вариант для отзыва военной тематики</label>
                  </div>
                  <button
                    class="btn btn-sm btn-outline-dark"
                    @click.prevent="saveSetting(setting, index, resource)"
                    :disabled="!isSettingValid(setting)"
                  >
                    <span v-if="setting.isNew">Создать</span>
                    <span v-else>Сохранить</span>
                  </button>
                </div>
              </div>

              <button
                class="btn btn-sm btn-outline-primary mt-2"
                @click.prevent="addSetting(resource)"
                :disabled="!canAddMoreSettings(resource)"
              >
                + Добавить настройку
              </button>
              <div v-if="!canAddMoreSettings(resource)" class="text-muted small mt-1">
                Достигнут лимит настроек для этого источника ({{ maxSettingsPerResource }})
              </div>
            </div>
          </div>

          <button
            v-else
            class="btn btn-sm btn-outline-dark"
            @click.prevent="addSetting(resource)"
          >
            Добавить настройки
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ProjectApi } from "@/api/projects";

export default {
  name: 'ProjectAutoReply',
  components: {},
  data() {
    return {
      resources: [],
      auto_replies: [],
      tempIdCounter: 0,
      maxSettingsPerResource: 10 // Максимальное количество настроек для одного источника
    }
  },
  computed: {
    hasTextNoDataAccess() {
      return this.$store.getters.tariff?.replay_data_check
    },
    project_id: {
      get() {
        return this.$store.getters.currentProjectId
      },
      set() {}
    }
  },
  mounted: function() {
    this.loadData();
  },
  methods: {
    generateTempId() {
      return ++this.tempIdCounter;
    },

    loadData() {
      ProjectApi.resource_all_list('has_auto_replay=true').then(response => {
        this.resources = response;

        ProjectApi.get_auto_replies(this.project_id).then(response => {
          this.auto_replies = response;
          this.resources.forEach(res => {
            const existingSettings = response.filter(x => x.resource === res.id);
            if (existingSettings.length) {
              existingSettings.forEach(setting => {
                setting.isNew = false;
                setting._tempId = null;
              });
              res.settings = existingSettings;
            } else {
              res.settings = [];
            }
          });
        });
      });
    },

    addSetting(resource) {
      if (!resource.settings) {
        resource.settings = [];
      }

      resource.settings.push({
        text: '',
        text_no_data: '',
        resource: resource.id,
        is_active: true,
        isNew: true,
        _tempId: this.generateTempId()
      });
    },

    isSettingValid(setting) {
      if (!setting) return false;
      return !(!setting.text && !setting.text_no_data);

    },

    canAddMoreSettings(resource) {
      if (!resource.settings) return true;
      return resource.settings.length < this.maxSettingsPerResource;
    },

    deleteSetting(resource, setting) {
      if (setting.isNew) {
        const index = resource.settings.indexOf(setting);
        if (index !== -1) {
          resource.settings.splice(index, 1);
        }

        if (resource.settings.length === 0) {
          resource.settings = [];
        }

        this.$notify({
          type: "info",
          text: 'Настройка удалена'
        });
        return;
      }

      if (setting.id) {
        if (confirm('Вы уверены, что хотите удалить эти настройки?')) {
          ProjectApi.delete_auto_reply(this.project_id, setting.id).then(() => {
            const index = resource.settings.indexOf(setting);
            if (index !== -1) {
              resource.settings.splice(index, 1);
            }

            if (resource.settings.length === 0) {
              resource.settings = [];
            }

            this.auto_replies = this.auto_replies.filter(x => x.id !== setting.id);

            this.$notify({
              type: "success",
              text: 'Настройки удалены'
            });
          }).catch(error => {
            this.$notify({
              type: "error",
              text: 'Ошибка при удалении: ' + (error.message || '')
            });
          });
        }
      }
    },

    saveSetting(setting, index, resource) {
      const saveData = {
        id: setting.id,
        resource: setting.resource,
        text: setting.text || '',
        text_no_data: setting.text_no_data || '',
        is_active: setting.is_active,
        war_marker: setting.war_marker,
      };

      ProjectApi.save_auto_replies(this.project_id, saveData).then(response => {
        resource.settings.splice(index, 1, response)
        this.$notify({
          type: "success",
          text: setting.isNew ? 'Настройка создана' : 'Настройки сохранены'
        });
      }).catch(error => {
        this.$notify({
          type: "error",
          text: 'Ошибка при сохранении: ' + (error.message || '')
        });
      });
    }
  }
}
</script>

<style scoped lang="scss">
.auto-reply-area {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding-top: 15px;

  .resource-item {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    padding: 15px;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }

    .settings {
      flex: 1;

      .settings-group {
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 100%;

        .setting-item {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 15px;
          background: #f8f9fa;
          transition: all 0.3s ease;

          .setting-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            .setting-index {
              font-weight: 600;
              font-size: 14px;
              color: #495057;
              display: flex;
              align-items: center;
              gap: 8px;

              .badge {
                font-size: 11px;
                padding: 3px 8px;
                border-radius: 12px;

                &.badge-warning {
                  background-color: #ffc107;
                  color: #856404;
                }

                &.badge-success {
                  background-color: #28a745;
                  color: white;
                }
              }
            }
          }

          .text-section {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-bottom: 15px;

            .form-group {
              width: 100%;

              textarea {
                width: 100%;
                resize: vertical;
              }
            }
          }

          .controls-section {
            display: flex;
            align-items: center;
            gap: 20px;
            flex-wrap: wrap;

            .checkbox {
              display: flex;
              align-items: center;
              gap: 8px;

              input[type="checkbox"] {
                margin: 0;
              }

              label {
                margin-bottom: 0;
              }
            }
          }
        }
      }
    }
  }
}

.settings-label {
  font-weight: 500;
  font-size: 14px;
  color: #495057;
  display: block;
  margin-bottom: 5px;
}

.text-danger {
  color: #dc3545;
}

.text-muted {
  color: #6c757d;
}

.small {
  font-size: 0.875em;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}
</style>